/**
 * Lightweight beep generator using the Web Audio API.
 *
 * Plays a clear, loud two-tone "ding" reminiscent of WhatsApp and triggers
 * a strong vibration pattern on phones that support it.
 *
 * Browsers block audio until the user has interacted with the page at least
 * once. `unlockAudio()` must be called on the first click/keydown so the
 * next message arrival actually plays.
 */
let audioCtx = null;
let unlocked = false;

function getCtx() {
  if (!audioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
  }
  return audioCtx;
}

export function unlockAudio() {
  if (unlocked) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  unlocked = true;
}

export function playMessageDing() {
  // Respect a per-browser mute toggle stored in localStorage.
  if (localStorage.getItem('jpd-sound') === 'off') return;

  // Strong haptic feedback on phones that support it.
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate([400, 100, 400, 100, 400]);
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.debug('[notifySound] vibrate:', e?.message);
  }

  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  // Louder peak (~0.6 vs 0.18 before) and one extra accent tone so it cuts
  // through phone-speaker silence even at low system volume.
  const tones = [
    { f: 1046, t: 0.00, d: 0.16 },  // C6
    { f: 784,  t: 0.18, d: 0.22 },  // G5
    { f: 1175, t: 0.42, d: 0.18 },  // D6 — sharp final accent
  ];
  for (const tone of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';  // richer harmonics than sine -> sounds louder
    osc.frequency.setValueAtTime(tone.f, now + tone.t);
    gain.gain.setValueAtTime(0.0001, now + tone.t);
    gain.gain.exponentialRampToValueAtTime(0.6, now + tone.t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.t + tone.d);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + tone.t);
    osc.stop(now + tone.t + tone.d + 0.02);
  }
}

export function isSoundEnabled() {
  return localStorage.getItem('jpd-sound') !== 'off';
}

export function setSoundEnabled(on) {
  localStorage.setItem('jpd-sound', on ? 'on' : 'off');
}
